import { useEffect, useState } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isReauthInProgress } from '../lib/reauth-state';

/**
 * useAuth — single source of truth for portal sign-in status.
 *
 * On mount: checks Cognito session via Amplify. If a valid session exists,
 * status becomes 'authenticated' and the JWT becomes available. Otherwise
 * 'unauthenticated'.
 *
 * Hub events: subscribes to Amplify auth Hub so the rest of the app
 * (AuthenticatedRoute, SignIn) react to sign-in / sign-out without prop
 * drilling.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  cognitoSub?: string;
  email?: string;
  staffId?: string;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    async function check(): Promise<void> {
      try {
        const session = await fetchAuthSession();
        const user = await getCurrentUser();
        if (cancelled) return;
        const claims = session.tokens?.idToken?.payload as
          | { sub?: string; email?: string; 'custom:staffId'?: string }
          | undefined;
        setState({
          status: 'authenticated',
          cognitoSub: claims?.sub ?? user.userId,
          email: claims?.email,
          staffId: claims?.['custom:staffId'],
        });
      } catch {
        if (cancelled) return;
        setState({ status: 'unauthenticated' });
      }
    }

    void check();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      // ADR-063: during MFA step-up re-auth the flow transiently signs the user
      // out (so Amplify's signIn can start a fresh auth event that advances
      // auth_time). Ignore that transient signedOut so AuthenticatedRoute does
      // not redirect and unmount the operator's in-progress action. The
      // subsequent signedIn restores the authenticated state.
      if (payload.event === 'signedOut' && isReauthInProgress()) {
        return;
      }
      // Re-check on sign-in / sign-out / token-refresh.
      if (
        payload.event === 'signedIn' ||
        payload.event === 'signedOut' ||
        payload.event === 'tokenRefresh'
      ) {
        void check();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return state;
}
